import {
  createQuery,
  keepPreviousData,
  useQueryClient,
} from "@tanstack/solid-query";
import { createFileRoute, Link } from "@tanstack/solid-router";
import { createMemo, createSignal, For, Show } from "solid-js";
import {
  IconCalendar,
  IconChartColumn,
  IconCircleDollarSign,
  IconEllipsisVertical,
  IconPrinter,
  IconTrendingDown,
  IconTrendingUp,
} from "~/components/icons";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { DatePickerField } from "~/components/ui/date-picker-field";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { MultiSelect } from "~/components/ui/multi-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import {
  businessDetailResource,
  businessesResource,
  useActiveBusinessId,
} from "~/features/business/business.resource";
import { getPnLPageData } from "~/features/reports/p-and-l-page";
import {
  defaultPnLParams,
  PnLResource,
  startOfYearIso,
  todayIso,
  type PnLData,
} from "~/features/reports/p-and-l.resource";
import { CLASS_FILTER_NONE } from "~/lib/class-filter";
import { withBusinessGuard } from "~/lib/route-guards";
import { cn, formatCurrency, formatDisplayCalendarDate } from "~/lib/utils";

export const Route = createFileRoute("/_dashboard/dashboard/reports/p-and-l")({
  staticData: {
    breadcrumb: "Profit & Loss",
  },
  ...withBusinessGuard(),
  loader: async ({ context }) => {
    const collection = await context.queryClient.ensureQueryData(
      businessesResource.options(undefined),
    );
    const businessId = collection.activeBusinessId!;

    // One server-side function call — aggregate fetches the default P&L and
    // business detail (for the class filter) in parallel. During SSR this is a
    // direct function call; on client navigation it's one HTTP round trip.
    const pageData = await getPnLPageData({ data: { businessId } });

    // Seed the query cache so the P&L component's createQuery hook finds data
    // on the first paint (including SSR on a hard refresh).
    businessDetailResource.seed(
      context.queryClient,
      businessId,
      pageData.businessDetail,
    );
    PnLResource.seedIfEmpty(
      context.queryClient,
      defaultPnLParams(businessId),
      pageData.pnl,
    );

    return {
      businessId,
      pnl: pageData.pnl,
      collection,
      activeBusiness: pageData.businessDetail,
    };
  },
  component: PnLPage,
});

function PnLPage() {
  const qc = useQueryClient();
  const data = Route.useLoaderData();

  // Surface loader data into the TQ cache synchronously during render, so the
  // P&L component's createQuery hooks never fire a network request on the
  // initial hydration pass. The loader seeded the cache on the server too, but
  // without a dehydration boundary the client starts fresh.
  const d = data();
  if (d) {
    businessDetailResource.seedIfEmpty(qc, d.businessId, d.activeBusiness);
    PnLResource.seedIfEmpty(qc, defaultPnLParams(d.businessId), d.pnl);
  }

  return <PnLReport />;
}

type PnLAccountLine = PnLData["revenue"][number];
type PeriodPreset = "ytd" | "thisYear" | "lastYear" | "custom";

const PERIOD_OPTIONS: { value: PeriodPreset; label: string }[] = [
  { value: "ytd", label: "Year to date" },
  { value: "thisYear", label: "This year" },
  { value: "lastYear", label: "Last year" },
  { value: "custom", label: "Custom range" },
];

function formatPercent(value: number | null): string {
  if (value === null) return "—";
  const sign = value > 0 ? "+" : "";
  return `${sign}${value.toFixed(1)}%`;
}

/** Percentage change, null when the comparison is zero (avoid div-by-zero). */
function percentChange(amount: number, comparison: number): number | null {
  if (comparison === 0) return null;
  return ((amount - comparison) / Math.abs(comparison)) * 100;
}

// Shared column layout for the statement: account name + three right-aligned
// numeric columns (current, comparison, change). Comparison column collapses
// on narrow screens; the container scrolls horizontally as a fallback.
const GRID =
  "grid grid-cols-[minmax(0,1fr)_6rem_6rem] sm:grid-cols-[minmax(0,1fr)_7rem_7rem_6rem] items-center gap-3 sm:gap-4";

/** Compact label for a period column: "2026" for a full year or YTD, else a range. */
function columnLabel(from: string, to: string): string {
  const f = new Date(`${from}T00:00:00Z`);
  const t = new Date(`${to}T00:00:00Z`);
  const isFullYear =
    f.getUTCMonth() === 0 &&
    f.getUTCDate() === 1 &&
    t.getUTCMonth() === 11 &&
    t.getUTCDate() === 31;
  const isYtd = f.getUTCMonth() === 0 && f.getUTCDate() === 1;
  if (isFullYear || isYtd) return String(f.getUTCFullYear());
  return `${formatDisplayCalendarDate(from, { style: "short" }) ?? ""} – ${
    formatDisplayCalendarDate(to, { style: "short" }) ?? ""
  }`;
}

function PnLReport() {
  const businessId = useActiveBusinessId();

  // Date range state. Defaults to the current calendar year-to-date.
  const [from, setFrom] = createSignal(startOfYearIso());
  const [to, setTo] = createSignal(todayIso());
  const [periodPreset, setPeriodPreset] = createSignal<PeriodPreset>("ytd");
  const [classIds, setClassIds] = createSignal<string[]>([]);

  const businessQuery = createQuery(() => ({
    ...businessDetailResource.options(businessId() ?? "", !!businessId()),
  }));
  const classes = () => businessQuery.data?.classes ?? [];

  const pnlQuery = createQuery(() => ({
    ...PnLResource.options(
      {
        businessId: businessId() ?? "",
        from: from(),
        to: to(),
        classIds: classIds(),
      },
      !!businessId(),
    ),
    // Keep the previous period's data visible while the new class/date range
    // is fetching, so the statement doesn't flash empty on every filter change.
    placeholderData: keepPreviousData,
  }));

  // Read the P&L data reactively from the live query. `pnlQuery.data` is backed
  // by solid-query's `queryResource.latest` proxy, which is reactive — it
  // updates whenever the query for the current class/date params resolves, and
  // with `keepPreviousData` it holds the prior period's data while a new range
  // is fetching (no flash). Reading it directly (rather than via a non-reactive
  // `qc.getQueryData` cache snapshot) keeps the memo correctly subscribed so a
  // background refetch for an already-cached key still retriggers the UI.
  const pnl = createMemo<PnLData | undefined>(() => pnlQuery.data);

  const applyPreset = (preset: PeriodPreset) => {
    setPeriodPreset(preset);
    const year = new Date().getFullYear();
    if (preset === "ytd") {
      setFrom(startOfYearIso());
      setTo(todayIso());
    } else if (preset === "thisYear") {
      setFrom(`${year}-01-01`);
      setTo(`${year}-12-31`);
    } else if (preset === "lastYear") {
      setFrom(`${year - 1}-01-01`);
      setTo(`${year - 1}-12-31`);
    }
    // "custom" leaves from/to as-is; the date pickers are shown for editing.
  };

  const hasFilters = () => classIds().length > 0 || periodPreset() !== "ytd";

  const resetFilters = () => {
    setPeriodPreset("ytd");
    setFrom(startOfYearIso());
    setTo(todayIso());
    setClassIds([]);
  };

  const periodLabel = createMemo(() => {
    const f = from();
    const t = to();
    if (!f || !t) return "";
    return `${formatDisplayCalendarDate(f, { style: "short" }) ?? ""} – ${
      formatDisplayCalendarDate(t, { style: "short" }) ?? ""
    }`;
  });

  const comparisonLabel = createMemo(() => {
    const cf = pnl()?.comparisonFrom;
    const ct = pnl()?.comparisonTo;
    if (!cf || !ct) return "";
    return `${formatDisplayCalendarDate(cf, { style: "short" }) ?? ""} – ${
      formatDisplayCalendarDate(ct, { style: "short" }) ?? ""
    }`;
  });

  return (
    <div class="flex flex-1 flex-col gap-6 px-4 sm:px-6 pb-3 pt-6">
      {/* Report header */}
      <div class="flex flex-col gap-4">
        <div class="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 class="text-2xl font-bold tracking-tight">Profit &amp; Loss</h1>
            <div class="mt-1 flex flex-wrap items-center gap-2">
              <Show when={periodLabel()}>
                <Badge
                  variant="outline"
                  class="gap-1.5 font-normal text-muted-foreground"
                >
                  <IconCalendar class="size-3" />
                  {periodLabel()}
                </Badge>
              </Show>
              <Show when={comparisonLabel()}>
                <span class="text-sm text-muted-foreground/70">vs.</span>
                <Badge
                  variant="outline"
                  class="gap-1.5 font-normal text-muted-foreground"
                >
                  <IconCalendar class="size-3" />
                  {comparisonLabel()}
                </Badge>
              </Show>
            </div>
          </div>
          <DropdownMenu placement="bottom-end">
            <DropdownMenuTrigger
              as={Button<"button">}
              variant="outline"
              size="icon"
              class="print:hidden"
            >
              <IconEllipsisVertical class="size-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent class="w-44">
              <DropdownMenuItem onClick={() => window.print()}>
                <IconPrinter class="size-4" />
                Print Report
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Toolbar */}
        <div class="flex flex-wrap items-center gap-2 print:hidden">
          <Select
            value={periodPreset()}
            onChange={(value) => value && applyPreset(value as PeriodPreset)}
            options={PERIOD_OPTIONS.map((o) => o.value)}
            itemComponent={(props) => (
              <SelectItem item={props.item}>
                {
                  PERIOD_OPTIONS.find((o) => o.value === props.item.rawValue)
                    ?.label
                }
              </SelectItem>
            )}
          >
            <SelectTrigger class="h-10 w-44">
              <SelectValue<string>>
                {(state) =>
                  PERIOD_OPTIONS.find((o) => o.value === state.selectedOption())
                    ?.label
                }
              </SelectValue>
            </SelectTrigger>
            <SelectContent />
          </Select>

          <div class="w-63">
            <MultiSelect
              options={[
                ...classes().map((c) => ({ label: c.name, value: c.id })),
                { label: "None", value: CLASS_FILTER_NONE },
              ]}
              placeholder="All classes"
              value={classIds()}
              onChange={setClassIds}
              multiple
            />
          </div>

          <Show when={periodPreset() === "custom"}>
            <div class="flex items-center gap-1.5">
              <span class="shrink-0 text-sm text-muted-foreground">From</span>
              <DatePickerField value={from()} onChange={setFrom} class="w-56" />
            </div>
            <div class="flex items-center gap-1.5">
              <span class="shrink-0 text-sm text-muted-foreground">To</span>
              <DatePickerField value={to()} onChange={setTo} class="w-56" />
            </div>
          </Show>

          <Show when={hasFilters()}>
            <Button variant="ghost" size="sm" onClick={resetFilters}>
              Reset
            </Button>
          </Show>
        </div>
      </div>

      {/* Financial statement */}
      {/* Use the function-as-children form of Show: Solid's Show only re-runs
          its children when `when` flips between falsy/truthy, NOT when a truthy
          value changes. Passing `data={pnl()!}` directly would pin the children
          to the first snapshot forever. The accessor form re-reads `pnl()`
          fresh on every render pass, so the statement updates on each fetch. */}
      <Show when={pnl()}>
        {(data) => (
          <>
            <PnLSummaryCards data={data()} />
            <PnLStatement data={data()} />
          </>
        )}
      </Show>
    </div>
  );
}

/**
 * At-a-glance KPI row. Lets someone read the headline result without
 * scanning the full statement, and surfaces margin, which the line-item
 * table never showed at all.
 */
function PnLSummaryCards(props: { data: PnLData }) {
  const margin = createMemo(() =>
    props.data.totalRevenue !== 0
      ? (props.data.netIncome / props.data.totalRevenue) * 100
      : null,
  );
  const comparisonMargin = createMemo(() =>
    props.data.comparisonTotalRevenue !== 0
      ? (props.data.comparisonNetIncome / props.data.comparisonTotalRevenue) *
        100
      : null,
  );

  const cards = createMemo(() => [
    {
      label: "Revenue",
      value: formatCurrency(props.data.totalRevenue),
      change: percentChange(
        props.data.totalRevenue,
        props.data.comparisonTotalRevenue,
      ),
      color: "var(--color-account-revenue)",
      rgb: "82,201,138",
      icon: IconTrendingUp,
    },
    {
      label: "Expenses",
      value: formatCurrency(props.data.totalExpenses),
      change: percentChange(
        props.data.totalExpenses,
        props.data.comparisonTotalExpenses,
      ),
      color: "var(--color-account-expense)",
      rgb: "240,180,106",
      icon: IconTrendingDown,
    },
    {
      label: "Net income",
      value: formatCurrency(props.data.netIncome),
      change: percentChange(
        props.data.netIncome,
        props.data.comparisonNetIncome,
      ),
      color: "var(--color-account-equity)",
      rgb: "62,207,174",
      icon: IconCircleDollarSign,
    },
    {
      label: "Net margin",
      value: margin() === null ? "—" : `${margin()!.toFixed(1)}%`,
      change:
        margin() !== null && comparisonMargin() !== null
          ? margin()! - comparisonMargin()!
          : null,
      isPoints: true,
      color: "var(--color-primary)",
      rgb: "120,120,140",
      icon: IconChartColumn,
    },
  ]);

  return (
    <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <For each={cards()}>
        {(card) => (
          <div class="relative flex flex-col overflow-hidden rounded-2xl border bg-card text-card-foreground shadow-sm">
            {/* Per-card top accent bar */}
            <div
              class="h-0.5 w-full shrink-0"
              style={{ background: card.color }}
            />

            {/* Card header */}
            <div class="px-5 pb-3 pt-5">
              <div class="mb-3.5 flex items-center gap-2.5">
                <div
                  class="flex size-8.5 shrink-0 items-center justify-center rounded-[10px]"
                  style={{
                    background: `rgba(${card.rgb},0.15)`,
                    color: card.color,
                  }}
                >
                  <card.icon size={16} />
                </div>
                <span class="text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
                  {card.label}
                </span>
              </div>

              <div class="flex items-end justify-between gap-3">
                <div class="font-mono text-[26px] font-medium tracking-[-0.02em]">
                  {card.value}
                </div>

                {/* Change badge on the right */}
                <div class="flex flex-col items-end gap-1">
                  <Show
                    when={card.change !== null}
                    fallback={
                      <span class="rounded-[5px] px-1.75 py-0.5 font-mono text-[11px] font-medium text-muted-foreground/60">
                        —
                      </span>
                    }
                  >
                    <span
                      class="rounded-[5px] px-1.75 py-0.5 font-mono text-[11px] font-medium"
                      style={{
                        background: `rgba(${card.rgb},0.15)`,
                        color: card.color,
                      }}
                    >
                      {card.change! > 0 ? "↑" : card.change! < 0 ? "↓" : ""}
                      {card.change! !== 0 ? " " : ""}
                      {card.isPoints
                        ? `${Math.abs(card.change!).toFixed(1)} pts`
                        : formatPercent(card.change!)}
                    </span>
                    <span class="text-[11px] text-muted-foreground">
                      vs. prior period
                    </span>
                  </Show>
                </div>
              </div>
            </div>
          </div>
        )}
      </For>
    </div>
  );
}

function PnLStatement(props: { data: PnLData }) {
  const currentLabel = createMemo(() =>
    columnLabel(props.data.from, props.data.to),
  );
  const comparisonLabel = createMemo(() =>
    columnLabel(props.data.comparisonFrom, props.data.comparisonTo),
  );

  return (
    <div class="overflow-x-auto rounded-lg border">
      <div class="min-w-140">
        {/* Column headers */}
        <div
          class={cn(
            GRID,
            "sticky top-0 z-10 border-b bg-muted/40 px-4 py-2.5 text-xs font-medium text-muted-foreground",
          )}
        >
          <span>Account</span>
          <span class="text-right">{currentLabel()}</span>
          <span class="hidden text-right sm:block">{comparisonLabel()}</span>
          <span class="text-right">Change</span>
        </div>

        <div class="px-4">
          <PnLSection
            title="Revenue"
            lines={props.data.revenue}
            total={props.data.totalRevenue}
            comparisonTotal={props.data.comparisonTotalRevenue}
          />
          <PnLSection
            title="Expenses"
            lines={props.data.expense}
            total={props.data.totalExpenses}
            comparisonTotal={props.data.comparisonTotalExpenses}
          />
        </div>

        {/* Net income */}
        <div
          class={cn(
            GRID,
            "border-t-2 bg-muted/20 px-4 py-3 text-sm font-semibold",
          )}
        >
          <span>Net Income</span>
          <span class="text-right tabular-nums">
            {formatCurrency(props.data.netIncome)}
          </span>
          <span class="hidden text-right tabular-nums text-muted-foreground sm:block">
            {formatCurrency(props.data.comparisonNetIncome)}
          </span>
          <span
            class="text-right tabular-nums text-muted-foreground"
            title={`${formatCurrency(
              props.data.netIncome - props.data.comparisonNetIncome,
            )} vs ${formatCurrency(props.data.comparisonNetIncome)}`}
          >
            {formatPercent(
              percentChange(
                props.data.netIncome,
                props.data.comparisonNetIncome,
              ),
            )}
          </span>
        </div>
      </div>
    </div>
  );
}

function PnLSection(props: {
  title: string;
  lines: PnLAccountLine[];
  total: number;
  comparisonTotal: number;
}) {
  return (
    <div class="py-1">
      {/* Section header */}
      <div class={cn(GRID, "py-2")}>
        <span class="text-sm font-semibold">{props.title}</span>
      </div>

      <Show
        when={props.lines.length > 0}
        fallback={
          <p class="py-2 pl-4 text-sm text-muted-foreground">
            No {props.title.toLowerCase()} accounts with activity in this
            period.
          </p>
        }
      >
        <div class="divide-y divide-border/60">
          <For each={props.lines}>
            {(line) => <AccountRow line={line} depth={0} />}
          </For>
        </div>
      </Show>

      {/* Subtotal */}
      <div class={cn(GRID, "border-t py-2.5 text-sm font-semibold")}>
        <span class="pl-4">Total {props.title}</span>
        <span class="text-right tabular-nums">
          {formatCurrency(props.total)}
        </span>
        <span class="hidden text-right tabular-nums text-muted-foreground sm:block">
          {formatCurrency(props.comparisonTotal)}
        </span>
        <span
          class="text-right tabular-nums text-muted-foreground"
          title={`${formatCurrency(
            props.total - props.comparisonTotal,
          )} vs ${formatCurrency(props.comparisonTotal)}`}
        >
          {formatPercent(percentChange(props.total, props.comparisonTotal))}
        </span>
      </div>
    </div>
  );
}

function AccountRow(props: { line: PnLAccountLine; depth: number }) {
  const hasChildren = () => props.line.children.length > 0;

  return (
    <>
      <div
        class={cn(
          GRID,
          "rounded py-1.5 text-sm hover:bg-muted/30",
          hasChildren() ? "font-medium" : "",
        )}
        style={{ "padding-left": `${props.depth * 1.25}rem` }}
      >
        <span class="flex items-center gap-2 truncate">
          <span class="shrink-0 font-mono text-[11px] text-muted-foreground/70">
            {props.line.code}
          </span>
          <Link
            to="/dashboard/accounts/$accountId"
            params={{ accountId: props.line.id }}
            class="truncate hover:underline"
          >
            {props.line.name}
          </Link>
        </span>
        <span class="text-right tabular-nums">
          {formatCurrency(props.line.amount)}
        </span>
        <span class="hidden text-right tabular-nums text-muted-foreground sm:block">
          {formatCurrency(props.line.comparisonAmount)}
        </span>
        <span
          class="text-right tabular-nums text-muted-foreground"
          title={`${formatCurrency(props.line.change)} vs ${formatCurrency(
            props.line.comparisonAmount,
          )}`}
        >
          {formatPercent(props.line.changePercent)}
        </span>
      </div>
      <Show when={hasChildren()}>
        <For each={props.line.children}>
          {(child) => <AccountRow line={child} depth={props.depth + 1} />}
        </For>
      </Show>
    </>
  );
}
